import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Divider, Input } from 'antd';
import { isEmpty, noop } from 'lodash';
import { FormattedMessage } from 'react-intl';

import FieldCheck from 'containers/TemplatePreviewPage/FieldCheck';
import messages from 'containers/TemplatePreviewPage/TemplateFlows/ComplexFlow/messages';

import QuestionHeader from '../QuestionHeader';
import UploadMedia from './UploadMedia';

const TextWithMedia = ({
  question,
  onUpdateVariable,
  hideHeader = false,
  hideErrors = false,
  skipTopMargin,
  onEnter = noop,
  isRepeating,
  autoFocus = false,
}) => {
  const {
    value = {},
    hasError = false,
    isPristine = true,
    placeholder = '',
    supportedMediaTypes,
  } = question;

  const { text: textValue = '', media: mediaValue = {} } = value;

  const onUpdate = useCallback(
    (text, media) => {
      const validMedia = isEmpty(media) ? undefined : media;
      onUpdateVariable({ text, media: validMedia });
    },
    [onUpdateVariable],
  );

  const onChangeText = useCallback(
    (event) => {
      const text = event.target.value;
      onUpdate(text, mediaValue);
    },
    [mediaValue, onUpdate],
  );
  const onChangeMedia = useCallback(
    (newMedia) => {
      onUpdate(textValue, newMedia);
    },
    [textValue, onUpdate],
  );

  return (
    <React.Fragment>
      {!hideHeader && (
        <QuestionHeader
          title={question.body}
          note={question.note}
          description={question.description}
          skipTopMargin={skipTopMargin}
        />
      )}
      <div className="flex flex-items-center w-full">
        <div className="flex-1 position-relative">
          <Input
            // eslint-disable-next-line react/no-array-index-key
            className={classNames(
              'w-full',
              'border-grey-5',
              'border-grey-5-hover',
              'border-blue-6-focus',
              'color-grey-8-placeholder',
              'border-radius-level-1',
              'color-grey-10',
              'pf-input-no-shadow',
              { 'js-repeating-field': isRepeating },
            )}
            autoFocus={autoFocus}
            placeholder={placeholder}
            value={textValue}
            onPressEnter={onEnter}
            onChange={onChangeText}
            style={{
              height: '48px',
              lineHeight: '48px',
            }}
          />
          <FieldCheck checked={!hasError} />
        </div>
        <Divider
          type="vertical"
          className="vertical-margin-none horizontal-margin-xs background-grey-5"
          style={{ height: 42 }}
        />

        <UploadMedia
          supportedMediaTypes={supportedMediaTypes}
          mediaValue={mediaValue}
          altText={textValue}
          onChangeMedia={onChangeMedia}
        />
      </div>

      {!isPristine && hasError && !hideErrors && (
        <div>
          <span className="color-red-6">
            <FormattedMessage {...messages.validation.fieldRequired} />
          </span>
        </div>
      )}
    </React.Fragment>
  );
};

TextWithMedia.propTypes = {
  question: PropTypes.object,
  questionIndex: PropTypes.number,
  onUpdateVariable: PropTypes.func.isRequired,
  hideHeader: PropTypes.bool,
  autoFocus: PropTypes.bool,
};

export default TextWithMedia;
